import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Box,
  Chip,
  LinearProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  IconButton
} from '@mui/material';
import {
  SmartToy as MLIcon,
  TrendingUp as TrendingUpIcon,
  ExpandMore as ExpandMoreIcon,
  Lightbulb as LightbulbIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { apiService } from '../../services/api';

interface MLRecommendationCardProps {
  story: {
    title: string;
    description?: string;
    estimatedSize?: string;
    complexityFactors?: {
      frontend: number;
      backend: number;
      middleware: number;
      database: number;
    };
  };
  teamId?: string;
  projectId?: string;
  onApply?: (recommendation: any) => void;
}

const MLRecommendationCard: React.FC<MLRecommendationCardProps> = ({
  story,
  teamId,
  projectId,
  onApply
}) => {
  const [recommendation, setRecommendation] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  const fetchRecommendation = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await apiService.getMLRecommendation({
        title: story.title,
        description: story.description,
        estimatedSize: story.estimatedSize,
        complexityFactors: story.complexityFactors,
        teamId,
        projectId
      });
      setRecommendation(result);
    } catch (err: any) {
      setError(err.message || 'Failed to get ML recommendation');
    } finally {
      setLoading(false);
    }
  };

  const handleApply = () => {
    if (recommendation && onApply) {
      onApply({
        recommendedStoryPoints: recommendation.recommendedStoryPoints,
        recommendedTShirtSize: recommendation.recommendedTShirtSize
      });
    }
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 0.7) return 'success';
    if (confidence >= 0.5) return 'warning';
    return 'error';
  };

  const getMethodologyLabel = (methodology: string) => {
    const labels: Record<string, string> = {
      'historical_pattern': 'Historical Patterns',
      'similarity_match': 'Similar Stories',
      'team_calibration': 'Team Calibration',
      'hybrid': 'Hybrid Approach'
    };
    return labels[methodology] || methodology;
  };

  return (
    <Card sx={{ mt: 2, border: '1px solid', borderColor: 'divider' }}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Box display="flex" alignItems="center" gap={1}>
            <MLIcon color="primary" />
            <Typography variant="h6">AI/ML Recommendation</Typography>
          </Box>
          {!recommendation && (
            <Button
              variant="outlined"
              size="small"
              onClick={fetchRecommendation}
              disabled={loading}
              startIcon={<LightbulbIcon />}
            >
              Get AI Suggestion
            </Button>
          )}
        </Box>

        {loading && <LinearProgress sx={{ mb: 2 }} />}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {recommendation && (
          <>
            <Box display="flex" gap={2} flexWrap="wrap" mb={2}>
              <Chip
                label={`${recommendation.recommendedStoryPoints} Story Points`}
                color="primary"
                size="medium"
                sx={{ fontWeight: 'bold' }}
              />
              <Chip
                label={recommendation.recommendedTShirtSize?.toUpperCase() || 'N/A'}
                color="secondary"
                size="medium"
              />
              <Chip
                icon={<TrendingUpIcon />}
                label={`${(recommendation.confidenceScore * 100).toFixed(0)}% Confidence`}
                color={getConfidenceColor(recommendation.confidenceScore)}
                size="medium"
              />
              <Chip
                label={getMethodologyLabel(recommendation.methodology)}
                variant="outlined"
                size="small"
              />
            </Box>

            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">{recommendation.reasoning}</Typography>
            </Alert>

            {recommendation.teamCalibrationAdjustment && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Team Calibration:</strong> {recommendation.teamCalibrationAdjustment.reason}
                </Typography>
              </Alert>
            )}

            {(recommendation.similarStories && recommendation.similarStories.length > 0) && (
              <Accordion expanded={expanded} onChange={() => setExpanded(!expanded)}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">
                    Similar Stories ({recommendation.similarStories.length})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <List dense>
                    {recommendation.similarStories.map((similar: any, index: number) => (
                      <ListItem key={index}>
                        <ListItemText
                          primary={similar.title}
                          secondary={
                            <>
                              Est: {similar.estimatedStoryPoints} pts | 
                              Actual: {similar.actualStoryPoints} pts | 
                              Similarity: {(similar.similarityScore * 100).toFixed(0)}%
                            </>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            )}

            {onApply && (
              <CardActions sx={{ justifyContent: 'flex-end', pt: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleApply}
                  startIcon={<CheckCircleIcon />}
                >
                  Apply Recommendation
                </Button>
              </CardActions>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default MLRecommendationCard;

